<template>
  <!-- Inline: form only, no dialog (used inside fiche modal). -->
  <div v-if="inline" class="InfrastructureEditModal InfrastructureEditModal--inline">
    <div class="InfrastructureEditModal__body p-0 overflow-y-auto">
      <div v-if="isLoadingAttributes" class="text-center py-4">
        <p class="text-sm text-gray-600">{{ t('infrastructure.fiche.loading') }}</p>
      </div>
      <form v-else id="infrastructure-edit-form" @submit.prevent="handleSubmit" class="space-y-4">
        <div
          v-for="attribut in availableAttributes"
          :key="attribut.atgId"
          class="InfrastructureEditModal__field"
        >
          <BaseSelect
            v-if="hasChoiceOptions(attribut)"
            :id="`field-${attribut.atgRefCode}`"
            :label="libelleDynamique(attribut.atgTitre, attribut.atgTitreEn) + (attribut.atgObligatoire ? ' *' : '')"
            :model-value="formData[attribut.atgRefCode]"
            @update:model-value="(value) => (formData[attribut.atgRefCode] = value)"
            :options="getChoiceOptions(attribut)"
            :placeholder="libelleDynamique(attribut.atgTitre, attribut.atgTitreEn)"
            width="w-full"
            :get-value="(opt) => opt.agcValeur"
            :get-label="(opt) => libelleDynamique(opt.agcValeur, opt.agcValeurEn) || opt.agcRefCode"
          />
          <BaseInput
            v-else
            :id="`field-${attribut.atgRefCode}`"
            :label="libelleDynamique(attribut.atgTitre, attribut.atgTitreEn) + (attribut.atgObligatoire ? ' *' : '')"
            :model-value="formData[attribut.atgRefCode]"
            @update:model-value="(value) => (formData[attribut.atgRefCode] = value)"
            :type="getInputType(attribut.atgNature)"
            :placeholder="libelleDynamique(attribut.atgTitre, attribut.atgTitreEn)"
            width="w-full"
          />
        </div>
        <div v-if="availableAttributes.length === 0" class="text-center py-4">
          <p class="text-sm text-gray-600">
            {{ t('infrastructure.fiche.noAttributes') }}
          </p>
        </div>
      </form>
    </div>
    <div
      class="InfrastructureEditModal__footer flex items-center justify-end gap-3 pt-4 mt-4 border-t border-gray-200"
    >
      <button
        type="button"
        class="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
        @click="handleClose"
      >
        {{ t('infrastructure.fiche.cancelButton') }}
      </button>
      <button
        type="button"
        :disabled="isSubmitting || isLoadingAttributes"
        class="px-4 py-2 text-sm font-medium text-white bg-main rounded-md hover:bg-main-hover disabled:opacity-50 disabled:cursor-not-allowed"
        @click="handleSubmit"
      >
        {{ isSubmitting ? t('infrastructure.fiche.saving') : t('infrastructure.fiche.saveButton') }}
      </button>
    </div>
  </div>

  <!-- Standalone: full dialog (e.g. from elsewhere). -->
  <TransitionRoot v-else appear :show="true" as="template">
    <Dialog as="div" class="relative z-50" @close="handleClose">
      <TransitionChild
        as="template"
        enter="duration-300 ease-out"
        enter-from="opacity-0"
        enter-to="opacity-100"
        leave="duration-200 ease-in"
        leave-from="opacity-100"
        leave-to="opacity-0"
      >
        <div class="fixed inset-0 bg-black/50" aria-hidden="true" />
      </TransitionChild>

      <div class="fixed inset-0 w-screen overflow-y-auto">
        <div class="flex min-h-full items-center justify-center p-4">
          <TransitionChild
            as="template"
            enter="duration-300 ease-out"
            enter-from="opacity-0 scale-95"
            enter-to="opacity-100 scale-100"
            leave="duration-200 ease-in"
            leave-from="opacity-100 scale-100"
            leave-to="opacity-0 scale-95"
          >
            <DialogPanel
              class="InfrastructureEditModal__panel w-full max-w-2xl transform rounded-lg bg-white text-left align-middle shadow-xl transition-all flex flex-col max-h-[90vh]"
            >
              <div
                class="InfrastructureEditModal__header flex items-center justify-between px-6 py-4 border-b border-gray-200"
              >
                <h2 class="text-lg font-semibold text-gray-900">
                  {{ t('infrastructure.fiche.editTitle') }}
                </h2>
                <button
                  type="button"
                  class="text-gray-400 hover:text-gray-600 transition-colors p-1 rounded-full hover:bg-gray-100 cursor-pointer"
                  :aria-label="t('infrastructure.fiche.closeAria')"
                  @click="handleClose"
                >
                  <Icon icon="mdi:close" class="w-6 h-6" />
                </button>
              </div>
              <div class="InfrastructureEditModal__body p-6 overflow-y-auto">
                <div v-if="isLoadingAttributes" class="text-center py-4">
                  <p class="text-sm text-gray-600">{{ t('infrastructure.fiche.loading') }}</p>
                </div>
                <form
                  v-else
                  id="infrastructure-edit-form-standalone"
                  @submit.prevent="handleSubmit"
                  class="space-y-4"
                >
                  <div
                    v-for="attribut in availableAttributes"
                    :key="attribut.atgId"
                    class="InfrastructureEditModal__field"
                  >
                    <BaseSelect
                      v-if="hasChoiceOptions(attribut)"
                      :id="`field-${attribut.atgRefCode}`"
                      :label="libelleDynamique(attribut.atgTitre, attribut.atgTitreEn) + (attribut.atgObligatoire ? ' *' : '')"
                      :model-value="formData[attribut.atgRefCode]"
                      @update:model-value="(value) => (formData[attribut.atgRefCode] = value)"
                      :options="getChoiceOptions(attribut)"
                      :placeholder="libelleDynamique(attribut.atgTitre, attribut.atgTitreEn)"
                      width="w-full"
                      :get-value="(opt) => opt.agcValeur"
                      :get-label="(opt) => libelleDynamique(opt.agcValeur, opt.agcValeurEn) || opt.agcRefCode"
                    />
                    <BaseInput
                      v-else
                      :id="`field-${attribut.atgRefCode}`"
                      :label="libelleDynamique(attribut.atgTitre, attribut.atgTitreEn) + (attribut.atgObligatoire ? ' *' : '')"
                      :model-value="formData[attribut.atgRefCode]"
                      @update:model-value="(value) => (formData[attribut.atgRefCode] = value)"
                      :type="getInputType(attribut.atgNature)"
                      :placeholder="libelleDynamique(attribut.atgTitre, attribut.atgTitreEn)"
                      width="w-full"
                    />
                  </div>
                  <div v-if="availableAttributes.length === 0" class="text-center py-4">
                    <p class="text-sm text-gray-600">
                      {{ t('infrastructure.fiche.noAttributes') }}
                    </p>
                  </div>
                </form>
              </div>
              <div
                class="InfrastructureEditModal__footer flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200"
              >
                <button
                  type="button"
                  class="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                  @click="handleClose"
                >
                  {{ t('infrastructure.fiche.cancelButton') }}
                </button>
                <button
                  type="button"
                  :disabled="isSubmitting || isLoadingAttributes"
                  class="px-4 py-2 text-sm font-medium text-white bg-main rounded-md hover:bg-main-hover disabled:opacity-50 disabled:cursor-not-allowed"
                  @click="handleSubmit"
                >
                  {{
                    isSubmitting
                      ? t('infrastructure.fiche.saving')
                      : t('infrastructure.fiche.saveButton')
                  }}
                </button>
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

<script setup lang="ts">
import Icon from '@/components/ui/Icon.vue'
import BaseInput from '@/components/ui/BaseInput.vue'
import BaseSelect from '@/components/ui/BaseSelect.vue'
import type { Infrastructure } from '@/models/interfaces/Infrastructure'
import {
  ATTRIBUT_KIND_EDITABLE,
  ATTRIBUT_NATURE_CHOICE,
  ATTRIBUT_NATURE_NUMERIC,
  ATTRIBUT_NATURE_TEXT,
  type Attribut,
} from '@/models/interfaces/Attribut'
import type { AttributChoix } from '@/models/interfaces/AttributChoix'
import { InfrastructureService } from '@/services/infrastructure/InfrastructureService'
import { AttributService } from '@/services/attribut/AttributService'
import { getChoicesByAttributId } from '@/services/attributChoix/AttributChoixService'
import { CompetenceTypeOuvrageService } from '@/services/competenceTypeOuvrage/CompetenceTypeOuvrageService'
import { TransitionRoot, TransitionChild, Dialog, DialogPanel } from '@headlessui/vue'
import { useI18n } from 'vue-i18n'
import { ref, watch } from 'vue'
import { POSITION, useToast } from 'vue-toastification'
import { libelleDynamique } from '@/utils/libelleDynamique'

const { t } = useI18n()
const toast = useToast()

const props = withDefaults(
  defineProps<{
    infrastructure: Infrastructure | null
    idInfrastructure: string | number | null
    competenceId?: number | null
    /** When true, render only the form + footer (no dialog). Use inside fiche modal. */
    inline?: boolean
  }>(),
  { inline: false },
)

const emit = defineEmits<{
  close: []
  /** Emitted with the payload that was just saved, so the fiche can display it if GET does not return attribute values yet. */
  saved: [payload: Record<string, string | number | boolean>]
}>()

const availableAttributes = ref<Attribut[]>([])
const choicesByAttributId = ref<Record<number, AttributChoix[]>>({})
const isLoadingAttributes = ref(false)
const isSubmitting = ref(false)
const formData = ref<Record<string, string | number | undefined>>({})

const EMPTY_CHOICE_OPTION: AttributChoix = {
  agcId: 0,
  fkAtgId: 0,
  agcRefCode: '—',
  agcValeur: '',
  agcStatut: 'ACTIF',
}

function hasChoiceOptions(attribut: Attribut): boolean {
  return (choicesByAttributId.value[attribut.atgId] ?? []).length > 0
}

function getChoiceOptions(attribut: Attribut): AttributChoix[] {
  const choices = (choicesByAttributId.value[attribut.atgId] ?? [])
    .slice()
    .sort((a, b) => (a.agcOrdre ?? 0) - (b.agcOrdre ?? 0))
  if (attribut.atgObligatoire) return choices
  return [EMPTY_CHOICE_OPTION, ...choices]
}

function getInputType(nature: string): string {
  const typeMap: Record<string, string> = {
    TXT: 'text',
    NUM: 'number',
    NUMERIC: 'number',
    DATE: 'date',
    BOOL: 'checkbox',
  }
  return typeMap[nature] || 'text'
}

function isNumericAttribut(attr: Attribut): boolean {
  return attr.atgNature === 'NUM' || attr.atgNature === ATTRIBUT_NATURE_NUMERIC
}

function handleClose() {
  emit('close')
}

/** Type ouvrage code from infrastructure (e.g. FORAGE, CIMETIERE). */
function getTypeOuvrageCode(infra: Infrastructure | null): string | undefined {
  if (!infra || typeof infra !== 'object') return undefined
  const rec = infra as Record<string, unknown>
  const raw =
    rec.ift_type_code ??
    rec.iftTypeCode ??
    (rec.typeOuvrage && typeof rec.typeOuvrage === 'object' && rec.typeOuvrage !== null
      ? ((rec.typeOuvrage as Record<string, unknown>).code ??
        (rec.typeOuvrage as Record<string, unknown>).libelle)
      : rec.type_ouvrage)
  return raw != null ? String(raw).trim() : undefined
}

/** Extract attribut ids from CompetenceTypeOuvrage (IRI or embedded). */
function extractAttributIds(cto: {
  attributs?: string[] | Array<{ atgId?: number; ['@id']?: string }>
}): number[] {
  const attributs = cto.attributs ?? (cto as Record<string, unknown>).attributs
  if (!Array.isArray(attributs)) return []
  return attributs
    .map((a) => {
      if (typeof a === 'string') {
        const match = a.match(/\/api\/attributs\/(\d+)/)
        return match ? parseInt(match[1], 10) : NaN
      }
      const obj = a as { atgId?: number }
      return obj?.atgId ?? (a as Record<string, unknown>).atgId
    })
    .filter((id): id is number => Number.isInteger(id))
}

/** Match CompetenceTypeOuvrage by competence and type ouvrage code. */
function typeOuvrageMatches(
  cto: {
    typeOuvrage?: { code?: string; libelle?: string } | string
    fkCptId?: number
    fkTypeOuvrageId?: number
  },
  typeCode: string,
  competenceId: number | null,
): boolean {
  // Sans compétence connue, le type d'ouvrage suffit (même règle que InfrastructureFicheContent) :
  // exiger une compétence écartait toutes les entrées et vidait le formulaire.
  const cptId = cto.fkCptId ?? (cto as Record<string, unknown>).fk_cpt_id
  if (competenceId !== null && cptId !== competenceId) return false
  const typeOuvrage = cto.typeOuvrage ?? (cto as Record<string, unknown>).type_ouvrage
  if (!typeOuvrage) return cto.fkTypeOuvrageId != null
  const code =
    typeof typeOuvrage === 'string'
      ? typeOuvrage
      : ((typeOuvrage as { code?: string; libelle?: string }).code ??
        (typeOuvrage as { code?: string; libelle?: string }).libelle)
  return code != null && String(code).toUpperCase() === String(typeCode).toUpperCase()
}

async function loadAvailableAttributes() {
  if (!props.idInfrastructure) return
  isLoadingAttributes.value = true
  try {
    const typeCode = getTypeOuvrageCode(props.infrastructure)
    const competenceId = props.competenceId ?? null

    // Fast path: one call returns the attributs for this (competence, type ouvrage) pair
    // with their CHOICE options already embedded (choices), so no per-attribut
    // GET /api/attribut_choixes round-trip is needed.
    // Le type d'ouvrage suffit : sans compétence (fiche ouverte par lien direct ou depuis le
    // tableau de bord), le repli ci-dessous ne peut rien trouver et le formulaire restait vide.
    if (typeCode) {
      availableAttributes.value = await CompetenceTypeOuvrageService.getAttributsForCompetenceAndType(
        competenceId,
        typeCode,
      )
      const byId: Record<number, AttributChoix[]> = {}
      availableAttributes.value.forEach((attr) => {
        if (attr.choices && attr.choices.length > 0) {
          byId[attr.atgId] = attr.choices as AttributChoix[]
        }
      })
      choicesByAttributId.value = byId
      initializeFormData()
      return
    }

    // Fallback (competenceId or typeCode unavailable): resolve attributs via
    // CompetenceTypeOuvrage / CompetenceAttribut, then fetch choices individually.
    const [allCto, allAttributs, competenceAttributs] = await Promise.all([
      CompetenceTypeOuvrageService.getAll({ expandAttributs: true }).catch(() => []),
      AttributService.getAll(),
      AttributService.getCompetenceAttributs().catch(() => []),
    ])

    const attributIds = new Set<number>()

    if (competenceId != null) {
      competenceAttributs
        .filter(
          (ca) =>
            (ca.fkCptId ?? (ca as unknown as Record<string, unknown>).fk_cpt_id) === competenceId,
        )
        .forEach((ca) => {
          const id = ca.fkAttributId ?? (ca as unknown as Record<string, unknown>).fk_attribut_id
          if (typeof id === 'number') attributIds.add(id)
        })
    }

    if (attributIds.size === 0) {
      const matchingCto = allCto.filter((cto) =>
        typeCode ? typeOuvrageMatches(cto, typeCode, competenceId) : false,
      )
      matchingCto.forEach((cto) => extractAttributIds(cto).forEach((id) => attributIds.add(id)))
    }

    availableAttributes.value = allAttributs.filter(
      (attr) => attr.atgKind === ATTRIBUT_KIND_EDITABLE && attributIds.has(attr.atgId),
    )

    const choiceResults = await Promise.all(
      availableAttributes.value
        .filter((attr) => attr.atgNature === ATTRIBUT_NATURE_CHOICE)
        .map(async (attr) => ({
          atgId: attr.atgId,
          choices: await getChoicesByAttributId(attr.atgId),
        })),
    )
    const byId: Record<number, AttributChoix[]> = {}
    choiceResults.forEach(({ atgId, choices }) => {
      byId[atgId] = choices
    })
    choicesByAttributId.value = byId

    initializeFormData()
  } catch (error) {
    console.error('[InfrastructureEditModal] Error loading attributes:', error)
    toast.error(t('infrastructure.fiche.loadError'), { position: POSITION.TOP_RIGHT })
  } finally {
    isLoadingAttributes.value = false
  }
}

function initializeFormData() {
  if (!props.infrastructure) return
  const data: Record<string, string | number | undefined> = {}
  availableAttributes.value.forEach((attr) => {
    const key = attr.atgRefCode
    const value = (props.infrastructure as Record<string, unknown>)[key] as
      | string
      | number
      | undefined
    data[key] = value ?? ''
  })
  formData.value = data
}

/**
 * Build payload for PUT attribute_values. Only includes valid values:
 * - NUMERIC: number only (empty/invalid → omitted; backend would skip anyway).
 * - DATE: non-empty string only (empty → omitted).
 * - BOOLEAN: true/false only (empty/invalid → omitted).
 */
function buildAttributeValuesPayload(): Record<string, string | number | boolean> {
  const payload: Record<string, string | number | boolean> = {}
  availableAttributes.value.forEach((attr) => {
    const key = attr.atgRefCode
    const raw = formData.value[key]
    if (raw === undefined) return
    if (isNumericAttribut(attr)) {
      const str = typeof raw === 'string' ? raw.trim() : String(raw ?? '')
      if (str === '') return
      const num = Number(str)
      if (!Number.isNaN(num)) payload[key] = num
      return
    }
    if (attr.atgNature === 'BOOL') {
      const rawVal = raw as string | number | boolean
      const isTrue =
        rawVal === true ||
        rawVal === 1 ||
        rawVal === 'true' ||
        (typeof rawVal === 'string' && rawVal.toLowerCase().trim() === 'true')
      const isFalse =
        rawVal === false ||
        rawVal === 0 ||
        rawVal === 'false' ||
        (typeof rawVal === 'string' && rawVal.toLowerCase().trim() === 'false')
      if (isTrue) payload[key] = true
      else if (isFalse) payload[key] = false
      return
    }
    if (attr.atgNature === 'DATE') {
      const s = typeof raw === 'string' ? raw.trim() : String(raw ?? '')
      if (s !== '') payload[key] = s
      return
    }
    if (
      attr.atgNature === ATTRIBUT_NATURE_CHOICE ||
      attr.atgNature === 'TXT' ||
      attr.atgNature === ATTRIBUT_NATURE_TEXT ||
      hasChoiceOptions(attr)
    ) {
      const s = typeof raw === 'string' ? raw.trim() : String(raw ?? '').trim()
      if (s !== '') payload[key] = s
      return
    }
    const s = typeof raw === 'string' ? raw.trim() : String(raw ?? '').trim()
    if (s !== '') payload[key] = s
  })
  return payload
}

async function handleSubmit() {
  if (!props.idInfrastructure) return
  if (isSubmitting.value) return
  const payload = buildAttributeValuesPayload()
  if (Object.keys(payload).length === 0) {
    toast.error(t('infrastructure.fiche.saveErrorNoData'), { position: POSITION.TOP_RIGHT })
    return
  }
  isSubmitting.value = true
  try {
    await InfrastructureService.putAttributeValues(props.idInfrastructure, payload)
    toast.success(t('infrastructure.fiche.saveSuccess'), { position: POSITION.TOP_RIGHT })
    emit('saved', payload)
  } catch (error) {
    console.error('[InfrastructureEditModal] Error saving attribute values:', error)
    const axiosError = error as { response?: { status?: number; data?: Record<string, unknown> } }
    const status = axiosError?.response?.status
    const data = axiosError?.response?.data
    const detail =
      typeof data?.detail === 'string'
        ? data.detail
        : typeof data?.message === 'string'
          ? data.message
          : typeof (data as { 'hydra:description'?: string })?.['hydra:description'] === 'string'
            ? (data as { 'hydra:description': string })['hydra:description']
            : ''
    const isIriError = status === 400 && /Unable to generate an IRI/i.test(detail)
    let message =
      status === 401
        ? t('infrastructure.fiche.saveError401')
        : status === 400 && isIriError
          ? t('infrastructure.fiche.saveError400Iri')
          : status === 400
            ? t('infrastructure.fiche.saveError400')
            : status === 404
              ? t('infrastructure.fiche.saveError404')
              : status === 500
                ? t('infrastructure.fiche.saveError500')
                : t('infrastructure.fiche.saveError')
    if (!isIriError && detail) message = `${message} ${detail}`
    toast.error(message, { position: POSITION.TOP_RIGHT })
  } finally {
    isSubmitting.value = false
  }
}

watch(() => props.infrastructure, initializeFormData, { immediate: true })
watch(
  () => [props.infrastructure, props.competenceId],
  () => {
    loadAvailableAttributes()
  },
  { immediate: true },
)
</script>

<style lang="scss" scoped>
.InfrastructureEditModal--inline {
  display: flex;
  flex-direction: column;
  gap: 0;
}

.InfrastructureEditModal--inline .InfrastructureEditModal__body {
  padding: 0;
}

.InfrastructureEditModal--inline .InfrastructureEditModal__footer {
  padding: 1rem 0 0;
  margin-top: 1rem;
  border-top: 1px solid #e5e7eb;
  gap: 0.75rem;
}

.InfrastructureEditModal__panel {
  min-height: 12rem;
  border-radius: 0.75rem;
  box-shadow:
    0 20px 25px -5px rgba(0, 0, 0, 0.1),
    0 8px 10px -6px rgba(0, 0, 0, 0.1);
}

.InfrastructureEditModal__header {
  flex-shrink: 0;
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid #e5e7eb;
  background: #fafafa;
  border-radius: 0.75rem 0.75rem 0 0;

  h2 {
    font-size: 1.125rem;
    font-weight: 600;
    letter-spacing: -0.02em;
    color: #111827;
  }
}

.InfrastructureEditModal__body {
  flex: 1 1 auto;
  padding: 1.25rem 1.5rem;
}

.InfrastructureEditModal__field {
  margin-bottom: 1.25rem;
  padding-bottom: 1.25rem;
  border-bottom: 1px solid #f3f4f6;

  &:last-of-type {
    margin-bottom: 0;
    padding-bottom: 0;
    border-bottom: none;
  }

  .InfrastructureEditModal--inline & {
    margin-bottom: 1rem;
    padding-bottom: 1rem;
  }

  .InfrastructureEditModal--inline &:last-of-type {
    margin-bottom: 0;
    padding-bottom: 0;
  }
}

.InfrastructureEditModal__footer {
  flex-shrink: 0;
  padding: 1rem 1.5rem;
  border-top: 1px solid #e5e7eb;
  background: #fafafa;
  border-radius: 0 0 0.75rem 0.75rem;
  gap: 0.75rem;

  button[type='button']:first-child {
    padding: 0.5rem 1rem;
    font-size: 0.875rem;
    font-weight: 500;
    color: #374151;
    background: #fff;
    border: 1px solid #d1d5db;
    border-radius: 0.5rem;
    transition:
      background 0.15s,
      border-color 0.15s;

    &:hover {
      background: #f9fafb;
      border-color: #9ca3af;
    }
  }

  button[type='button']:last-child {
    padding: 0.5rem 1.25rem;
    font-size: 0.875rem;
    font-weight: 600;
    color: #fff;
    background: rgb(var(--v-theme-main-blue));
    border: none;
    border-radius: 0.5rem;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
    transition:
      box-shadow 0.15s,
      opacity 0.15s;

    &:hover:not(:disabled) {
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    }

    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }
  }
}

.InfrastructureEditModal--inline .InfrastructureEditModal__footer {
  background: transparent;
  padding-left: 0;
  padding-right: 0;
}
</style>
